/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useAuthKeys } from 'src/core/hooks/useAuthKeys';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { ResourceAssignmentDto, ResourceAssignmentForRegionSwagger, ResourceDto } from 'src/core/services/resourceAssignmentSwaggers';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { getEnvironment } from 'src/core/utils/api/environment';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { getResourceInfo } from 'src/core/utils/resourceAssignmentUtils';
import {
    getCustomTextPredictionCurlRequest,
    getCustomTextPredictionUrl
} from 'src/customText/core/services/testModels/customTextTestModelService';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';
import { ModelDeployLayout } from 'src/modules/modelDeploy/ModelDeployLayout';

const messages = defineMessages({
    deploymentFailed: {
        id: 'customText.customTextDeployModelPage.deploymentFailed',
        defaultMessage: 'Deploying "{deploymentName}" failed'
    },
    deploymentInProgress: {
        id: 'customText.customTextDeployModelPage.deploymentInProgress',
        defaultMessage: 'Deploying "{deploymentName}"...'
    },
    deploymentCompleted: {
        id: 'customText.customTextDeployModelPage.deploymentCompleted',
        defaultMessage: 'Deploying "{deploymentName}" completed'
    },
    deleteDeploymentFailed: {
        id: 'customText.customTextDeployModelPage.deleteDeploymentFailed',
        defaultMessage: 'Deleting deployment "{deploymentName}" failed'
    },
    deleteDeploymentInProgress: {
        id: 'customText.customTextDeployModelPage.deleteDeploymentInProgress',
        defaultMessage: 'Deleting deployment "{deploymentName}"...'
    },
    deleteDeploymentCompleted: {
        id: 'customText.customTextDeployModelPage.deleteDeploymentCompleted',
        defaultMessage: 'Deleting deployment "{deploymentName}" completed'
    },
    addResourceAssignmentCompleted: {
        id: 'customText.customTextDeployModelPage.addResourceAssignmentCompleted',
        defaultMessage: 'Resource is assigned successfully.'
    },
    addResourceAssignmentFailed: {
        id: 'customText.customTextDeployModelPage.addResourceAssignmentFailed',
        defaultMessage: 'Resource assignment failed.'
    },
    editResourceAssignmentCompleted: {
        id: 'customText.customTextDeployModelPage.editResourceAssignmentCompleted',
        defaultMessage: 'Resource assignment is updated successfully.'
    },
    editResourceAssignmentFailed: {
        id: 'customText.customTextDeployModelPage.editResourceAssignmentFailed',
        defaultMessage: 'Updating Resource assignment failed.'
    },
    removeResourceAssignmentCompleted: {
        id: 'customText.customTextDeployModelPage.removeResourceAssignmentCompleted',
        defaultMessage: 'Resource assignment is removed successfully.'
    },
    removeResourceAssignmentFailed: {
        id: 'customText.customTextDeployModelPage.removeResourceAssignmentFailed',
        defaultMessage: 'Removing Resource assignment failed.'
    }
});

export const CustomTextDeployModelPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const deleteTracker = useProgressTracker({ onComplete: () => setDeletedDeploymentsIds([]) });
    const {
        projectKind,
        name: projectName,
        deploymentsStoreHouse,
        resourceAssignmentsStoreHouse,
        trainedModels: trainedModelStoreHouse
    } = rootStore.currentCustomTextProjectStore;

    const [currentResourceDto, updateCurrentResourceDto] = useUpdateState<ResourceDto>({
        resource: rootStore.azureStore.session.csAccountStore,
        resourceId: rootStore.azureStore.session.csAccountStore.path
    });

    const { authorizationHeader } = useAuthKeys({
        currentServiceKey: rootStore.azureStore.session.serviceKey,
        currentResource: rootStore.azureStore.session.csAccountStore
    });

    const [deletedDeploymentsIds, setDeletedDeploymentsIds] = React.useState([]);
    const [selectedResourceSubscriptionId, setSelectedResourceSubscriptionId] = React.useState<string>(null);
    const { resources, resourcesTracker } = useAzureResourcesManagement({
        configs: { resources: true },
        subscriptionId: selectedResourceSubscriptionId
    });

    const environmentOptions = React.useMemo(
        () => ({
            currentResourceKind: currentResourceDto.resource.kind,
            currentResourceEndpoint: currentResourceDto.resource?.endpoint ?? 'https://YOUR_RESOURCE_ENDPOINT/'
        }),
        [currentResourceDto.resource]
    );
    const endpointUrl = React.useMemo(() => getCustomTextPredictionUrl(getEnvironment, environmentOptions), [currentResourceDto.resource]);

    const retrieveCode = React.useMemo(
        () =>
            `curl ${endpointUrl.replace('jobs', 'jobs/<OPERATION-ID>')} -H "${authorizationHeader.key}: ${authorizationHeader.value ??
                'YOUR_ENDPOINT_KEY'}" `,
        [endpointUrl, authorizationHeader.key, authorizationHeader.value]
    );

    React.useEffect(() => {
        if (resourcesTracker.isCompleted) {
            updateCurrentResourceDto({ resource: resources.find(resource => resource.path === currentResourceDto.resourceId) });
        }
    }, [currentResourceDto.resourceId, resourcesTracker.state]);

    React.useEffect(() => {
        dispatcher.dispatch('getCustomTextDeployments');
        if (FeatureToggles.MultiRegionDeployments) {
            dispatcher.dispatch('getCustomTextProjectAssignedResources');
        }
    }, []);

    const submitCode = React.useCallback(
        (deploymentName: string) =>
            getCustomTextPredictionCurlRequest({
                projectName,
                projectKind,
                deploymentName,
                getEnvironment,
                environmentOptions,
                authorizationHeader
            }),
        [endpointUrl, getEnvironment, environmentOptions, projectName, projectKind, authorizationHeader.key, authorizationHeader.value]
    );

    const deploy = (deploymentInfo: {
        deploymentName: string;
        modelName: string;
        tracker: ProgressTrackerStore;
        resources: ResourceAssignmentForRegionSwagger[];
    }) => {
        dispatcher.dispatch('deployCustomTextModel', {
            tracker: deploymentInfo.tracker,
            trainedModelName: deploymentInfo.modelName,
            deploymentName: deploymentInfo.deploymentName,
            assignedResourceIds: deploymentInfo.resources.map(resource => resource.azureResourceId),
            toastData: {
                failed: { title: localize(messages.deploymentFailed, { deploymentName: deploymentInfo.deploymentName }) },
                running: { title: localize(messages.deploymentInProgress, { deploymentName: deploymentInfo.deploymentName }) },
                completed: { title: localize(messages.deploymentCompleted, { deploymentName: deploymentInfo.deploymentName }) }
            }
        });
    };

    const handleDeleteDeployment = (deploymentName: string) => {
        dispatcher.dispatch('deleteCustomTextDeployment', {
            deploymentName,
            tracker: deleteTracker,
            toastData: {
                failed: { title: localize(messages.deleteDeploymentFailed, { deploymentName }) },
                running: { title: localize(messages.deleteDeploymentInProgress, { deploymentName }) },
                completed: { title: localize(messages.deleteDeploymentCompleted, { deploymentName }) }
            }
        });

        setDeletedDeploymentsIds([...deletedDeploymentsIds, deploymentName]);
    };

    const handleDeploymentsSwap = (sourceDeploymentName: string, targetDeploymentName: string, tracker: ProgressTrackerStore) =>
        dispatcher.dispatch('swapCustomTextDeployments', { sourceDeploymentName, targetDeploymentName, tracker });

    const handleAddResourceAssignment = (assignmentData: ResourceAssignmentDto, tracker: ProgressTrackerStore) => {
        dispatcher.dispatch('addCustomTextProjectResourceAssignment', {
            tracker,
            assignmentData,
            toastData: {
                failed: { title: localize(messages.addResourceAssignmentFailed) },
                completed: { title: localize(messages.addResourceAssignmentCompleted) }
            }
        });
    };

    const handleUpdateResourceAssignment = (
        assignmentData: ResourceAssignmentDto,
        oldResourceId: string,
        tracker: ProgressTrackerStore
    ) => {
        dispatcher.dispatch('updateCustomTextProjectResourceAssignment', {
            tracker,
            oldResourceId,
            assignmentData,
            toastData: {
                failed: { title: localize(messages.editResourceAssignmentFailed) },
                completed: { title: localize(messages.editResourceAssignmentCompleted) }
            }
        });
    };

    const handleRemoveResourceAssignment = (assignmentData: Partial<ResourceAssignmentStore>, tracker: ProgressTrackerStore) => {
        dispatcher.dispatch('deleteCustomTextProjectResourceAssignment', {
            tracker,
            assignmentData,
            toastData: {
                failed: { title: localize(messages.removeResourceAssignmentFailed) },
                completed: { title: localize(messages.removeResourceAssignmentCompleted) }
            }
        });
    };

    const onChangeSelectedResource = (id: string) => {
        const { subscriptionId } = getResourceInfo(id);
        setSelectedResourceSubscriptionId(subscriptionId);
        updateCurrentResourceDto({ resourceId: id });
    };

    return (
        <ModelDeployLayout
            projectName={projectName}
            projectType={projectKind}
            onDeploySubmit={deploy}
            submitCode={submitCode}
            retrieveCode={retrieveCode}
            predictUrl={() => endpointUrl}
            onSwapDeployments={handleDeploymentsSwap}
            onDeleteDeployment={handleDeleteDeployment}
            isServiceKeyTypeValid
            deletedDeploymentsIds={deletedDeploymentsIds}
            deploymentsStoreHouse={deploymentsStoreHouse}
            isLoadingResources={resourcesTracker.isRunning}
            trainedModelStoreHouse={trainedModelStoreHouse}
            onChangeSelectedResource={onChangeSelectedResource}
            onAddResourceAssignment={handleAddResourceAssignment}
            onEditResourceAssignment={handleUpdateResourceAssignment}
            regionDeploymentStoreHouse={resourceAssignmentsStoreHouse}
            onRemoveResourceAssignment={handleRemoveResourceAssignment}
        />
    );
});
